import React, { useEffect, useMemo } from "react";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import {
  DarkColors,
  IconButton,
  LightColors,
  MicOff,
  MicOffHover,
  MicOn,
  SemanticDarkColors,
  Tooltip,
  Typography,
} from "@thingsw/pitta-design-system";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Webviewer } from "../contants/Breakpoints";

import Switch from "@material-ui/core/Switch";
import { useSelector } from "react-redux";
import { RootState } from "../features/store";
import { THEME } from "../features/Theme/slice";

import ReactLoading from "react-loading";

interface MicToggleProps {
  enableMic: boolean;
  volume: number;
  disabled?: boolean;
  loading?: boolean;
  onToggleMic: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  grayCircle: {
    backgroundColor: DarkColors.primary["4"],
    borderRadius: "50%",
    width: 28,
    height: 28,
    position: "relative",
  },
  blueCircle: {
    backgroundColor: DarkColors.primary["7"],
    borderRadius: "50%",
    width: 28,
    height: 28,
    position: "relative",
  },
  ctrlIcon: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  loadingIcon: {
    position: "absolute",
    top: 2,
    right: 2,
  },
}));

const useSwitchStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 64,
    height: 32,
    padding: 0,
    overflow: "visible",
  },
  switchBase: {
    padding: 2,
    cursor: "default",
  },
  track: {
    backgroundColor: SemanticDarkColors.primary["0"],
    opacity: "1 !important",
    borderRadius: 20,
    position: "relative",
    "&:before, &:after": {
      display: "inline-block",
      position: "absolute",
      top: "55%",
      width: "50%",
      transform: "translateY(-50%)",
      textAlign: "center",
      fontSize: 12,
      fontFamily: "Roboto",
    },
    "&:before": {
      content: '"ON"',
      left: 1,
      opacity: 0,
      color: LightColors.primary["0"],
    },
    "&:after": {
      content: '"OFF"',
      right: 1,
      color: DarkColors.primary["4"],
    },
  },
  disabled: {
    "& + $track": {
      backgroundColor: `${SemanticDarkColors.primary["0"]}!important`,
    },
  },
  checked: {
    "&$switchBase": {
      transform: "translateX(32px)",
      padding: 2,
      "&:hover": {
        backgroundColor: "rgba(24,90,257,0.08)",
      },
    },
    "& + $track": {
      background: `${SemanticDarkColors.primary["0"]} !important`,
      "&:before": {
        opacity: 1,
      },
      "&:after": {
        opacity: 0,
      },
    },
  },
}));

const MicToggle = (props: MicToggleProps) => {
  const { enableMic, volume, disabled, loading, onToggleMic } = props;
  const theme = useTheme() as Theme;
  const classes = useStyles(props);

  const switchClasses = useSwitchStyles();
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const { t } = useTranslation();
  const [isHover /*, setIsHover*/] = React.useState(false);
  const [openMicTooltip, setOpenMicTooltip] = React.useState(false);
  const [openMicOffTooltip, setOpenMicOffTooltip] = React.useState(false);

  const { fullscreen } = useSelector((state: RootState) => state[THEME]);

  useEffect(() => {
    if (mobile) {
      // mantis - 9178, dependency에 enableMic가 포함 -> enableMic가 변경이 있을 때 마다 실행되어 enableMic 제거.
      // mobile or app인 경우 처음시작을 제외하고 툴팁이 나오지 않도록 수정 (Leehj)
      setOpenMicTooltip(true);
      setTimeout(() => setOpenMicTooltip(false), 3000);

      setOpenMicOffTooltip(true);
      setTimeout(() => setOpenMicOffTooltip(false), 3000);
    }
  }, [mobile]);

  // loading 상태 추가되면서 PC에서 tooltip 자동으로 닫히지 않아 timer로직 추가
  useEffect(() => {
    if (openMicTooltip) {
      const timer = setTimeout(() => setOpenMicTooltip(false), 3000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [openMicTooltip]);

  useEffect(() => {
    if (openMicOffTooltip) {
      const timer = setTimeout(() => setOpenMicOffTooltip(false), 3000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [openMicOffTooltip]);

  // mic off인 상태
  const micOffIcon = useMemo(() => {
    console.log("MicToggle", "disabled", disabled);
    let button = (
      <IconButton
        className={classes.ctrlIcon}
        disabled={disabled || loading}
        disableHover={mobile}
      >
        {(!mobile && openMicOffTooltip) || (mobile && isHover) ? (
          <MicOffHover
            style={{
              zIndex: 2,
              fontSize: 20,
            }}
          />
        ) : (
          <MicOff
            style={{
              zIndex: 2,
              fontSize: 20,
            }}
          />
        )}
      </IconButton>
    );
    if (loading) {
      button = (
        <div className={classes.loadingIcon}>
          <ReactLoading type="spinningBubbles" width={24} height={24} />
        </div>
      );
    }

    return (
      <Tooltip
        open={openMicOffTooltip}
        onOpen={() => setOpenMicOffTooltip(true)}
        onClose={() => setOpenMicOffTooltip(false)}
        disableTouchListener={mobile}
        disableHoverListener={mobile}
        PopperProps={{
          disablePortal: true,
          style: { width: 100 },
          modifiers: {
            preventOverflow: { enabled: false },
            offset: {
              enabled: true,
              offset: mobile && openMicOffTooltip ? "0, -20px" : "0, -10px",
            },
            flip: {
              enabled: false,
            },
          },
        }}
        title={
          <Typography category="Default" variant="Caption">
            {t("Start Voice Call")}
          </Typography>
        }
        placement={fullscreen ? "bottom" : "top"}
      >
        <div
          className={classes.grayCircle}
          // onTouchStart={() => (mobile ? setIsHover(true) : {})}
          onClick={() => {
            // if (mobile) {
            //   setIsHover(false);
            // }
            onToggleMic();
          }}
        >
          {button}
        </div>
      </Tooltip>
    );
  }, [
    classes.ctrlIcon,
    classes.grayCircle,
    classes.loadingIcon,
    disabled,
    fullscreen,
    isHover,
    loading,
    mobile,
    onToggleMic,
    openMicOffTooltip,
    t,
  ]);

  // mic on인 상태
  const micIcon = useMemo(() => {
    let button = (
      <IconButton className={classes.ctrlIcon} disabled={loading}>
        <MicOn
          volume={volume}
          style={{
            zIndex: 2,
            fontSize: 20,
          }}
          fill="none"
        />
      </IconButton>
    );
    if (loading) {
      button = (
        <div className={classes.loadingIcon}>
          <ReactLoading type="spinningBubbles" width={24} height={24} />
        </div>
      );
    }
    return (
      <Tooltip
        open={openMicTooltip}
        onOpen={() => setOpenMicTooltip(true)}
        onClose={() => setOpenMicTooltip(false)}
        disableTouchListener={mobile}
        disableHoverListener={mobile}
        PopperProps={{
          disablePortal: true,
          style: { width: 100 },
          modifiers: {
            preventOverflow: { enabled: false },
            offset: {
              enabled: true,
              offset: mobile && openMicTooltip ? "0, -20px" : "0, -10px",
            },
            flip: {
              enabled: false,
            },
          },
        }}
        title={
          <Typography category="Default" variant="Caption">
            {t("End Voice Call")}
          </Typography>
        }
        placement={fullscreen ? "bottom" : "top"}
      >
        <div
          className={classes.blueCircle}
          onClick={() => {
            onToggleMic();
          }}
        >
          {button}
        </div>
      </Tooltip>
    );
  }, [
    classes.blueCircle,
    classes.ctrlIcon,
    classes.loadingIcon,
    fullscreen,
    loading,
    mobile,
    onToggleMic,
    openMicTooltip,
    t,
    volume,
  ]);

  return (
    <div>
      <Switch
        classes={switchClasses}
        checked={enableMic}
        icon={micOffIcon}
        checkedIcon={micIcon}
        disabled={disabled || loading}
      />
    </div>
  );
};

export default MicToggle;
